// OEMs -- the OEM master (M2, owner B4/B5, D197).
//
// Everyone reads; Sales and Admin add an OEM; an Admin edits one, adds a
// spelling (alias), merges a duplicate into another and (de)activates. Nothing
// is ever deleted. ⚠️ THE SCREEN IS NOT THE GUARD: every rule and role is the
// server's (models/oem_master.py); a refusal is shown as it came.
//
// FORWARD-ONLY: OEM names already typed on old OEM quotes, requests,
// confirmations and orders are never rewritten. Any such name that names no
// OEM is listed under "Not linked to an OEM" with "Add as alias" (Admin).

import { useCallback, useEffect, useState } from "react";
import {
  getOems, saveOem, addOemAlias, mergeOem, setOemActive, type OemMaster, type OemUnlinked,
} from "@/services/api";
import { useAdminView, useCanEditCustomersOrFalse } from "@/auth/AuthContext";
import { HowItWorks } from "@/components/DealProgress";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box", minWidth: 0,
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[12]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BUTTON, border: "none", backgroundColor: colors.indigo600, color: semanticColors.onAccent };
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.xs,
  color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
};
const TD: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top" };
const CURRENCIES = ["", "INR", "USD", "EUR", "CNY"];

type Draft = { id?: number; name: string; currency: string; contact_name: string; contact_email: string; contact_phone: string; notes: string };
const blank: Draft = { name: "", currency: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" };

export default function OemsPage() {
  const canCreate = useCanEditCustomersOrFalse();
  const { isAdmin } = useAdminView();
  const [rows, setRows] = useState<OemMaster[] | null>(null);
  const [unlinked, setUnlinked] = useState<OemUnlinked[]>([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [aliasFor, setAliasFor] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const r = await getOems(true);
    if (!r.ok) { setRows(null); setError(r.error ?? "The OEMs could not be read."); return; }
    setError(""); setRows(r.oems ?? []); setUnlinked(r.unlinked ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const act = async (p: Promise<{ ok: boolean; error?: string }>, done: string) => {
    const r = await p;
    if (!r.ok) { setError(r.error ?? "Not saved."); setMsg(""); return false; }
    setError(""); setMsg(done); await load(); return true;
  };
  const save = async () => {
    if (!draft) return;
    const ok = await act(saveOem({ ...draft, currency: draft.currency || null }), draft.id ? "OEM saved." : "OEM added.");
    if (ok) setDraft(null);
  };
  const active = (rows ?? []).filter((o) => o.active);

  return (
    <div data-testid="oems-page" style={{ padding: spacing[24] }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: spacing[12], marginBottom: spacing[16], flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
            color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight }}>OEMs</h1>
          <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary }}>
            The manufacturers and suppliers we buy from, each with every spelling in use.
          </div>
        </div>
        {canCreate && !draft && <button data-testid="oem-master-new" style={PRIMARY} onClick={() => setDraft({ ...blank })}>New OEM</button>}
      </div>

      <HowItWorks storageKey="anixify.howItWorks.oems" testId="oems-how">
        <div>OEM = the manufacturer or supplier we buy from. One OEM can be spelled several ways ("Lumen-Fibre",
          "LumenFibre Ltd"): each spelling is an <strong>alias</strong> of the same OEM, so OEM quotes, price requests
          and orders typed either way are recognised as one.</div>
        <div>Names typed before this list existed are never changed. Any that name no OEM are listed below as
          "Not linked to an OEM"; an Admin links each one with <strong>Add as alias</strong>.</div>
      </HowItWorks>

      {error && <div role="alert" data-testid="oems-error" style={{ ...NOTE, color: colors.red600, marginBottom: spacing[8] }}>{error}</div>}
      {msg && <div data-testid="oems-msg" style={{ ...NOTE, marginBottom: spacing[8] }}>{msg}</div>}

      {draft && (
        <div data-testid="oem-master-form" style={{ padding: spacing[12], marginBottom: spacing[16], border: `1px solid ${semanticColors.border}`,
          borderRadius: radius.base, backgroundColor: semanticColors.panelBg, display: "grid", gap: spacing[8] }}>
          <div style={{ display: "grid", gap: spacing[8], gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {([["name", "Name"], ["contact_name", "Contact name"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"]] as const).map(([k, l]) => (
              <label key={k} style={{ ...NOTE, display: "grid", gap: spacing[2] }}>{l}
                <input data-testid={`oem-master-${k}`} style={INPUT} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
              </label>
            ))}
            <label style={{ ...NOTE, display: "grid", gap: spacing[2] }}>Default currency
              <select data-testid="oem-master-currency" style={INPUT} value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
              </select>
            </label>
          </div>
          <label style={{ ...NOTE, display: "grid", gap: spacing[2] }}>Notes
            <textarea data-testid="oem-master-notes" style={{ ...INPUT, height: heights.controlLg * 2, padding: spacing[8] }}
              value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </label>
          <div style={{ display: "flex", gap: spacing[8] }}>
            <button data-testid="oem-master-save" style={PRIMARY} onClick={() => { void save(); }}>{draft.id ? "Save OEM" : "Add OEM"}</button>
            <button data-testid="oem-master-cancel" style={BUTTON} onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}

      {rows === null ? <div style={NOTE}>{error ? "" : "Loading…"}</div> : rows.length === 0 ? (
        <div data-testid="oems-empty" style={{ ...NOTE, padding: spacing[16], border: `1px dashed ${semanticColors.border}`, borderRadius: radius.base }}>
          No OEMs yet. Add the OEMs you buy from; then OEM quotes, price requests and orders can pick one from the list.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table data-testid="oems-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["OEM", "Spellings (aliases)", "Currency", "Contact", "Status", ""].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((o) => (
              <tr key={o.id} data-testid={`oem-master-row-${o.id}`} style={{ opacity: o.active ? 1 : 0.6 }}>
                <td style={{ ...TD, fontWeight: typography.fontWeight.medium }}>{o.name}</td>
                <td style={TD}>{o.aliases.filter((a) => a !== o.name).join(", ") || "—"}
                  {isAdmin && o.active && (
                    <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[4] }}>
                      <input aria-label={`New spelling for ${o.name}`} data-testid={`oem-alias-input-${o.id}`} style={{ ...INPUT, width: 160 }}
                        value={aliasFor[o.id] ?? ""} onChange={(e) => setAliasFor({ ...aliasFor, [o.id]: e.target.value })} />
                      <button data-testid={`oem-alias-add-${o.id}`} style={BUTTON} onClick={() => {
                        void act(addOemAlias(o.id, aliasFor[o.id] ?? ""), "Spelling added.").then((ok) => ok && setAliasFor({ ...aliasFor, [o.id]: "" }));
                      }}>Add spelling</button>
                    </div>
                  )}
                </td>
                <td style={TD}>{o.currency ?? "—"}</td>
                <td style={TD}>{[o.contact_name, o.contact_email, o.contact_phone].filter(Boolean).join(" · ") || "—"}</td>
                <td style={TD}>{o.active ? "Active" : "Inactive"}</td>
                <td style={{ ...TD, whiteSpace: "nowrap" }}>
                  {isAdmin && <button data-testid={`oem-master-edit-${o.id}`} style={BUTTON} onClick={() => setDraft({
                    id: o.id, name: o.name, currency: o.currency ?? "", contact_name: o.contact_name ?? "",
                    contact_email: o.contact_email ?? "", contact_phone: o.contact_phone ?? "", notes: o.notes ?? "" })}>Edit</button>}{" "}
                  {isAdmin && <button data-testid={`oem-master-active-${o.id}`} style={BUTTON}
                    onClick={() => { void act(setOemActive(o.id, !o.active), o.active ? "OEM deactivated." : "OEM reactivated."); }}>
                    {o.active ? "Deactivate" : "Reactivate"}</button>}{" "}
                  {isAdmin && o.active && active.length > 1 && (
                    <select aria-label={`Merge ${o.name} into`} data-testid={`oem-master-merge-${o.id}`} style={INPUT} value=""
                      onChange={(e) => {
                        const into = Number(e.target.value);
                        if (into && window.confirm(`Merge ${o.name} into ${active.find((x) => x.id === into)?.name}? Its spellings move there and ${o.name} is deactivated.`)) {
                          void act(mergeOem(o.id, into), "OEMs merged.");
                        }
                      }}>
                      <option value="">Merge into…</option>
                      {active.filter((x) => x.id !== o.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  )}
                </td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}

      <section data-testid="oems-unlinked" style={{ marginTop: spacing[24] }}>
        <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
          Not linked to an OEM
        </div>
        <div style={{ ...NOTE, marginBottom: spacing[8] }}>OEM names already typed on records that name no OEM in the list. They stay as typed;
          linking adds the name as a spelling of an OEM.</div>
        {unlinked.length === 0 ? <div data-testid="oems-unlinked-none" style={NOTE}>None: every OEM name in use is linked.</div> : (
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>{unlinked.map((u) => (
              <tr key={u.text} data-testid={`oem-unlinked-${u.text}`}>
                <td style={TD}>{u.text}</td><td style={{ ...TD, color: semanticColors.textMuted }}>{u.uses} record{u.uses === 1 ? "" : "s"}</td>
                <td style={TD}>{isAdmin && active.length > 0 ? (
                  <select aria-label={`Add ${u.text} as a spelling of`} data-testid={`oem-unlinked-add-${u.text}`} style={INPUT} value=""
                    onChange={(e) => { const id = Number(e.target.value); if (id) void act(addOemAlias(id, u.text), `"${u.text}" linked.`); }}>
                    <option value="">Add as alias of…</option>
                    {active.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>) : <span style={NOTE}>An Admin links it.</span>}</td>
              </tr>))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
